// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Route, ClassId } from '../types';
import { esc, num, pct, pctv, money, flag, shortName, fmtValue, SHARE_RAMP, NO_DATA } from '../format';
import { navigate } from '../main';

const RANK_TIER = (rank: number): number => (rank === 1 ? 4 : rank <= 3 ? 3 : rank <= 6 ? 2 : rank <= 12 ? 1 : 0);

export function renderAtlas(root: HTMLElement, d: Dataset, route: Route): void {
  const order = d.meta.classOrder;
  const store = 'au-foreign-owned:atlas-sort';
  let sortCls: string = localStorage.getItem(store) || 'score';
  let showAll = false;

  function draw(): void {
    const all = d.matrix.countries.slice();
    all.sort((a, b) => {
      if (sortCls === 'score') return b.score - a.score;
      const k = sortCls as ClassId;
      const av = a.byClass[k]?.value ?? -1, bv = b.byClass[k]?.value ?? -1;
      return bv - av;
    });
    const rows = showAll ? all : all.slice(0, 25);

    const head = `<tr><th class="row-h" style="cursor:default">Country</th>${order.map((cid) =>
      `<th class="col-h sortable" data-sort="${cid}" title="Sort by ${esc(d.meta.classMeta[cid].label)}">${esc(d.meta.classMeta[cid].short)}${sortCls === cid ? ' ▾' : ''}</th>`).join('')}</tr>`;

    const body = rows.map((cn) => {
      const cells = order.map((cid) => {
        const cell = cn.byClass[cid];
        if (!cell) return `<td class="mcell empty" aria-label="not present">·</td>`;
        const tier = RANK_TIER(cell.rank);
        const bg = SHARE_RAMP[tier];
        const dark = tier >= 2;
        const valStr = cid === 'commercial' && cell.dollars != null ? `${num(cell.value)} sites · ${money(cell.dollars)}` : fmtValue(cid, cell.value);
        const tip = `${cn.name} — ${d.meta.classMeta[cid].label}\nRank #${cell.rank} of ${d.classes[cid].countries.length} · ${pct(cell.share)} of foreign-held\n${valStr}${cell.changePct != null ? `\n${cell.changePct >= 0 ? '+' : ''}${(cell.changePct * 100).toFixed(0)}% vs prior year` : ''}`;
        return `<td class="mcell${cell.rank === 1 ? ' rank1' : ''}" data-cls="${cid}" data-country="${esc(cn.name)}" data-tip="${esc(tip)}" style="background:${bg};color:${dark ? '#fff' : 'var(--text-secondary)'}">${cell.rank}</td>`;
      }).join('');
      return `<tr><th class="row-h" data-country="${esc(cn.name)}"><span class="flagemoji">${flag(cn.iso)}</span>${esc(shortName(cn.name))}</th>${cells}</tr>`;
    }).join('');

    root.querySelector('.matrix-wrap')!.innerHTML = `<table class="matrix"><thead>${head}</thead><tbody>${body}</tbody></table>`;
    root.querySelector<HTMLButtonElement>('[data-toggle]')!.textContent = showAll ? `Show top 25` : `Show all ${all.length}`;
    wire();
  }

  function wire(): void {
    root.querySelectorAll<HTMLElement>('[data-sort]').forEach((th) => th.addEventListener('click', () => {
      sortCls = th.dataset.sort as ClassId; localStorage.setItem(store, sortCls); draw();
    }));
    root.querySelectorAll<HTMLElement>('.row-h[data-country]').forEach((th) => th.addEventListener('click', () => navigate({ country: th.dataset.country })));
    root.querySelectorAll<HTMLElement>('.mcell[data-country]').forEach((td) => td.addEventListener('click', () => navigate({ country: td.dataset.country })));
  }

  root.innerHTML = `
    <div class="view-head">
      <h2>The Atlas — who owns what</h2>
      <p>Every source country (rows) against every asset class (columns). Each cell shows that country's <strong>rank</strong> within the class; darker means higher. Read across a row and watch a country's rank swing from the top of one class to the bottom of another — that inversion is the whole story. Click a country for its full profile, or a column heading to sort by it.</p>
    </div>
    <div class="controls">
      <span class="control-label">Sort by</span>
      <div class="seg">
        <button data-sort2="score" aria-pressed="${sortCls === 'score'}">Overall</button>
        ${order.map((cid) => `<button data-sort2="${cid}" aria-pressed="${sortCls === cid}">${esc(d.meta.classMeta[cid].short)}</button>`).join('')}
      </div>
      <button class="chip" data-toggle style="cursor:pointer;border:1px solid var(--border-default);padding:5px 11px">Show all</button>
    </div>
    <div class="panel"><div class="matrix-wrap"></div>
      <div class="legend">
        <span class="legend-item">Rank in class:</span>
        <span class="legend-item"><span class="legend-swatch" style="background:${SHARE_RAMP[4]}"></span>#1</span>
        <span class="legend-item"><span class="legend-swatch" style="background:${SHARE_RAMP[3]}"></span>2–3</span>
        <span class="legend-item"><span class="legend-swatch" style="background:${SHARE_RAMP[2]}"></span>4–6</span>
        <span class="legend-item"><span class="legend-swatch" style="background:${SHARE_RAMP[1]}"></span>7–12</span>
        <span class="legend-item"><span class="legend-swatch" style="background:${SHARE_RAMP[0]}"></span>13+</span>
        <span class="legend-item"><span class="legend-swatch" style="background:${NO_DATA}"></span>not present</span>
        <span class="legend-item">★ = leads the class</span>
      </div>
    </div>
  `;

  root.querySelectorAll<HTMLElement>('[data-sort2]').forEach((b) => b.addEventListener('click', () => {
    sortCls = (b.dataset.sort2 as ClassId | 'score');
    localStorage.setItem(store, sortCls);
    root.querySelectorAll<HTMLElement>('[data-sort2]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    draw();
  }));
  root.querySelector<HTMLElement>('[data-toggle]')!.addEventListener('click', () => { showAll = !showAll; draw(); });

  // deep-link: open a country drawer when arriving with ?c=
  draw();
  void pctv; void route;
}
